// Cohort misconception-clustering observability — one row per clustering run.
//
// Reads the DEDICATED `cohort-cluster-runs` container (OperationRun records),
// not `async-jobs`. See docs/FeatureDesigns/operation-run-records.md.
//
// The "Coverage" column is why this list exists rather than being a filter on
// Model Runs. Clustering fails SILENTLY: a grouping that covered 3 of 200
// observations returns `succeeded` and renders to the teacher as "your class
// shares almost nothing" — indistinguishable from a healthy class. A green run
// is exactly the run nobody opens, so the number that contradicts it belongs on
// the row, not in a detail page.
//
// "Invented ids" is the second signal. buildClusters already discards ids the
// model made up, so they never reach a teacher — but a model inventing
// references is a prompt or schema problem worth seeing before it gets worse.
import { useMemo, useState } from 'react';
import { useOpenOperationRow } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi, type CohortClusterRunView } from '../../api/adminApi';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  succeeded: 'success',
  failed: 'error',
  running: 'warning',
};

const formatLatency = (ms?: number) => (ms ? `${(ms / 1000).toFixed(1)}s` : '—');
const formatCost = (c?: number) => (typeof c === 'number' ? `$${c.toFixed(4)}` : '—');

/** Coverage below this on a completed run is a near-miss worth flagging: the run
 *  succeeded and told the teacher almost nothing. */
const LOW_COVERAGE = 0.25;

export function CohortClusterList() {
  const [status, setStatus] = useState('');
  const openRow = useOpenOperationRow();

  const { data, isLoading, error } = useQuery({
    queryKey: ['cohortClusterRuns', status],
    queryFn: () => adminApi.getCohortClusterRuns({ status: status || undefined, limit: 50 }),
  });

  const rows = data?.items ?? [];

  const columns = useMemo<ColumnDef<CohortClusterRunView>[]>(() => [
    {
      key: 'started',
      header: 'Started',
      render: (r) => format(new Date(r.startedAt), 'MMM d, HH:mm:ss'),
    },
    {
      key: 'teacher',
      header: 'Teacher',
      render: (r) => (
        <div>
          <div>{r.teacherName || '—'}</div>
          {r.teacherEmail && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.teacherEmail}</div>
          )}
        </div>
      ),
    },
    {
      key: 'cohort',
      header: 'Cohort',
      render: (r) => (
        <div>
          <div>{r.subject || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.cohortId}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge variant={STATUS_BADGE[r.status] ?? 'default'} size="sm">
          {r.status}
        </Badge>
      ),
    },
    {
      key: 'clusters',
      header: 'Clusters',
      render: (r) => {
        const o = r.outcome;
        if (!o) return '—';
        const dropped = o.groupsReturned - o.clustersKept;
        return (
          <div>
            <div>{o.clustersKept} kept</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {o.groupsReturned} returned
              {dropped > 0 && ` · ${dropped} dropped`}
            </div>
          </div>
        );
      },
    },
    {
      key: 'coverage',
      header: 'Coverage',
      render: (r) => {
        const o = r.outcome;
        if (!o) return '—';
        // A completed run with no coverage number means nothing was sent — say
        // so rather than rendering a 0% that looks like a bad grouping.
        if (r.coverage === undefined) {
          return <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>nothing sent</span>;
        }
        const pct = Math.round(r.coverage * 100);
        return (
          <div>
            <Badge variant={r.coverage < LOW_COVERAGE ? 'error' : 'success'} size="sm">
              {pct}%
            </Badge>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {o.observationsClustered} of {o.observationsSent}
            </div>
          </div>
        );
      },
    },
    {
      key: 'invented',
      header: 'Invented ids',
      render: (r) => {
        const o = r.outcome;
        if (!o) return '—';
        const bad = o.unknownIds + o.duplicateIds;
        if (bad === 0) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
        return (
          <div style={{ color: 'var(--color-error, #c2321f)', fontSize: 12 }}>
            {o.unknownIds > 0 && <div>{o.unknownIds} unknown</div>}
            {o.duplicateIds > 0 && <div>{o.duplicateIds} re-used</div>}
          </div>
        );
      },
    },
    {
      key: 'model',
      header: 'Model / tokens',
      render: (r) =>
        r.llm ? (
          <div>
            <div>{r.llm.provider || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {r.llm.inputTokens}→{r.llm.outputTokens} · {formatCost(r.llm.costUsd)}
            </div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'latency',
      header: 'Latency',
      render: (r) => formatLatency(r.latencyMs),
    },
    {
      key: 'error',
      header: 'Error',
      render: (r) =>
        r.error ? (
          <span style={{ color: 'var(--color-error, #c2321f)', fontSize: 12 }}>{r.error}</span>
        ) : (
          '—'
        ),
    },
  ], []);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}>
          Status:{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
          </select>
        </label>
      </div>

      {error ? (
        <div style={{ color: 'var(--color-error, #c2321f)' }}>Failed to load clustering runs.</div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => openRow(rows, r, '/cohortcluster')}
          emptyMessage="No clustering runs yet."
        />
      )}
    </div>
  );
}
